// Field-level change history for one employee (History tab on the profile).
// Read-only audit view: who changed what, old → new, where the change came
// from (edit / import / approval workflow), and why. Manager+ only.
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardHeader, CardTitle } from '@/features/shared/components/ui/card';
import { Badge } from '@/features/shared/components/ui/badge';
import { History, Loader2, ShieldCheck } from 'lucide-react';
import {
  getEmployeeChangeHistory, humanizeField, type ChangeHistoryEntry,
} from '@/services/changeRequestService';

const val = (v: string | null | undefined) => (v == null || v === '' ? '—' : v);

const fmtDate = (d?: string | null) =>
  d
    ? new Date(d).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' })
    : '—';

export const ChangeHistoryCard: React.FC<{ employeeId: string }> = ({ employeeId }) => {
  const { t } = useTranslation('employees');
  const [history, setHistory] = useState<ChangeHistoryEntry[]>([]);
  const [loading, setLoading] = useState(true);
  // 'denied' (403/404: not yours to see) is told apart from 'unavailable' (any
  // other failure) — a refusal must never read as an outage (Muse CH1).
  const [error, setError] = useState<false | 'denied' | 'unavailable'>(false);
  const [fieldFilter, setFieldFilter] = useState('');

  useEffect(() => {
    let alive = true;
    setLoading(true);
    setError(false);
    getEmployeeChangeHistory(employeeId)
      .then((h) => { if (alive) setHistory(h); })
      .catch((e) => {
        if (!alive) return;
        const status = (e as { response?: { status?: number } })?.response?.status;
        setError(status === 403 || status === 404 ? 'denied' : 'unavailable');
      })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [employeeId]);

  // Distinct fields present in this employee's history, for the filter.
  const fieldOptions = useMemo(
    () => Array.from(new Set(history.map((h) => h.field))).sort(),
    [history],
  );
  const rows = useMemo(
    () => (fieldFilter ? history.filter((h) => h.field === fieldFilter) : history),
    [history, fieldFilter],
  );

  return (
    <Card className="border-orange-100 dark:border-orange-500/20">
      <CardHeader>
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <CardTitle className="text-base text-orange-700 flex items-center gap-2">
            <History className="w-4 h-4" /> {t('detail.change_history', { defaultValue: 'Change history' })}
          </CardTitle>
          <select
            aria-label={t('detail.history_filter_field', { defaultValue: 'Filter by field' })}
            value={fieldFilter}
            onChange={(e) => setFieldFilter(e.target.value)}
            className="h-9 px-3 rounded-md border border-border bg-card text-sm max-w-[220px]"
          >
            <option value="">{t('detail.history_all_fields', { defaultValue: 'All fields' })}</option>
            {fieldOptions.map((f) => (
              <option key={f} value={f}>{humanizeField(f)}</option>
            ))}
          </select>
        </div>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="flex items-center justify-center py-10 text-muted-foreground">
            <Loader2 className="w-5 h-5 animate-spin mr-2" />
            {t('detail.history_loading', { defaultValue: 'Loading change history…' })}
          </div>
        ) : error === 'denied' ? (
          <p className="text-sm text-muted-foreground py-6 text-center" data-testid="history-denied">
            {t('detail.history_denied', { defaultValue: "You don't have access to this employee's change history." })}
          </p>
        ) : error ? (
          <p className="text-sm text-muted-foreground py-6 text-center" data-testid="history-unavailable">
            {t('detail.history_error', { defaultValue: 'Change history is unavailable right now.' })}
          </p>
        ) : rows.length === 0 ? (
          <p className="text-sm text-muted-foreground py-6 text-center">
            {t('detail.history_empty', { defaultValue: 'No recorded changes yet.' })}
          </p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border text-left text-xs text-muted-foreground">
                  <th className="py-2 pr-4 font-medium">{t('detail.history_field', { defaultValue: 'Field' })}</th>
                  <th className="py-2 pr-4 font-medium">{t('detail.history_change', { defaultValue: 'Change' })}</th>
                  <th className="py-2 pr-4 font-medium">{t('detail.history_source', { defaultValue: 'Source' })}</th>
                  <th className="py-2 pr-4 font-medium">{t('detail.history_date', { defaultValue: 'Date' })}</th>
                  <th className="py-2 font-medium">{t('detail.history_reason', { defaultValue: 'Reason' })}</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((h) => (
                  <tr key={h.id} className="border-b border-border last:border-0 align-top">
                    <td className="py-2.5 pr-4 font-medium text-foreground whitespace-nowrap">
                      {humanizeField(h.field)}
                    </td>
                    <td className="py-2.5 pr-4">
                      <span className="text-muted-foreground break-all">{val(h.old_value)}</span>
                      <span className="mx-1.5 text-muted-foreground">→</span>
                      <span className="text-foreground break-all">{val(h.new_value)}</span>
                    </td>
                    <td className="py-2.5 pr-4">
                      <div className="flex items-center gap-1.5 flex-wrap">
                        <Badge variant="outline" className="text-xs capitalize">
                          {h.source || t('detail.history_source_edit', { defaultValue: 'edit' })}
                        </Badge>
                        {h.change_request_id && (
                          <Badge className="text-xs bg-orange-100 dark:bg-orange-500/[0.12] text-orange-800 dark:text-orange-300 border border-orange-200 dark:border-orange-500/25 flex items-center gap-1">
                            <ShieldCheck className="w-3 h-3" />
                            {t('detail.history_via_approval', { defaultValue: 'via approval' })}
                          </Badge>
                        )}
                      </div>
                    </td>
                    <td className="py-2.5 pr-4 whitespace-nowrap text-muted-foreground">
                      {fmtDate(h.effective_date || h.created_at)}
                    </td>
                    <td className="py-2.5 text-muted-foreground">{h.reason || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </CardContent>
    </Card>
  );
};
